import { useEffect, useRef, useState } from "preact/hooks";
import {
	type Slot,
	type SlotsByDay,
	useCampaignAvailability,
} from "../queries/useCampaignAvailability";
import { formatMonthDay, formatTime, formatWeekday, prettyTz } from "./format";

const WINDOW_SIZE = 4;

type SelectStepProps = {
	campaignId: string;
	timezone: string;
	tzOptions: string[];
	onTimezoneChange: (tz: string) => void;
	onPick: (slot: Slot, advisorName: string, advisorId: number) => void;
};

export function SelectStep({
	campaignId,
	timezone,
	tzOptions,
	onTimezoneChange,
	onPick,
}: SelectStepProps) {
	const [page, setPage] = useState(1);
	const [allDays, setAllDays] = useState<SlotsByDay[]>([]);
	const [windowStart, setWindowStart] = useState(0);
	const [selectedDate, setSelectedDate] = useState<string | null>(null);
	const [advisorName, setAdvisorName] = useState("");
	const [advisorId, setAdvisorId] = useState(0);

	const { data, isLoading, isError } = useCampaignAvailability(
		campaignId,
		timezone,
		page,
	);

	const sessionKey = `${campaignId}:${timezone}`;
	const sessionRef = useRef("");

	useEffect(() => {
		setAllDays([]);
		setPage(1);
		setWindowStart(0);
		setSelectedDate(null);
	}, [sessionKey]);

	useEffect(() => {
		setAdvisorName("");
		setAdvisorId(0);
	}, [campaignId]);

	useEffect(() => {
		if (!data) return;

		const isNewSession = sessionRef.current !== sessionKey;
		sessionRef.current = sessionKey;

		if (data.advisorName) setAdvisorName(data.advisorName);
		if (data.advisorId) setAdvisorId(data.advisorId);

		if (isNewSession) {
			setPage(1);
			setWindowStart(0);
			setSelectedDate(data.days[0]?.date ?? null);
			setAllDays(data.days);
		} else {
			setAllDays((prev) => {
				const existingDates = new Set(prev.map((d) => d.date));
				const newDays = data.days.filter((d) => !existingDates.has(d.date));
				return newDays.length === 0 ? prev : [...prev, ...newDays];
			});
		}
	}, [data, sessionKey]);

	const visibleDays = allDays.slice(windowStart, windowStart + WINDOW_SIZE);
	const skeletonCount = isLoading
		? Math.max(0, WINDOW_SIZE - visibleDays.length)
		: 0;

	const canGoLeft = windowStart > 0;
	const canGoRight =
		windowStart + WINDOW_SIZE < allDays.length ||
		(!!data?.hasMore && !isLoading);

	const goLeft = () => {
		if (canGoLeft) setWindowStart((s) => s - 1);
	};

	const goRight = () => {
		const nextStart = windowStart + 1;
		setWindowStart(nextStart);
		if (nextStart + WINDOW_SIZE >= allDays.length && data?.hasMore) {
			setPage((p) => p + 1);
		}
	};

	const selectedDay = allDays.find((d) => d.date === selectedDate) ?? null;
	const isEmpty = allDays.length === 0 && !!data && data.days.length === 0;
	const initialLoading = allDays.length === 0 && !isEmpty && !isError;

	return (
		<div>
			<p className="font-semibold tracking-[0.16em] uppercase text-xs text-[#9c8230]">
				Step 01 — Schedule
			</p>
			<h1 className='font-black tracking-tight leading-[0.95] font-["Gotham_Narrow","Helvetica_Neue",Arial,sans-serif] mt-3 text-5xl sm:text-6xl'>
				PICK A TIME.
			</h1>
			<div className="mt-4 max-w-md text-base text-[#2c4239]">
				{!advisorName ? (
					<>
						<div className="h-5 w-72 animate-pulse rounded bg-[#d8d59f]/60" />
						<div className="mt-2 h-5 w-44 animate-pulse rounded bg-[#d8d59f]/60" />
					</>
				) : (
					<>
						<p>
							A 15-minute introductory call with{" "}
							<span className="font-semibold">{advisorName}</span>.
						</p>
						<p>Choose any available slot.</p>
					</>
				)}
			</div>

			<div className="mt-10 border-t border-[#1a2e26]/15 pt-6">
				<div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
					<div>
						<p className="font-semibold tracking-[0.16em] uppercase text-xs text-[#2c4239]">
							Available times
						</p>
						<p className="mt-1 text-sm text-[#2c4239]">
							All times shown in your selected timezone.
						</p>
					</div>
					<label className="flex flex-col gap-1.5 text-xs text-[#2c4239]">
						<span className="font-semibold tracking-[0.16em] uppercase">
							Timezone
						</span>
						<select
							value={timezone}
							onChange={(e) =>
								onTimezoneChange((e.target as HTMLSelectElement).value)
							}
							className="min-w-[16rem] rounded-full border border-[#1a2e26]/30 bg-transparent py-2 pl-4 pr-4 text-sm focus:border-[#1a2e26] focus:outline-none focus:ring-2 focus:ring-[#c8a738]/40"
						>
							{tzOptions.map((tz) => (
								<option key={tz} value={tz}>
									{prettyTz(tz)}
								</option>
							))}
						</select>
					</label>
				</div>

				{initialLoading && <SlotSkeleton />}

				{!isLoading && isError && allDays.length === 0 && (
					<p className="mt-8 text-sm text-[#9c2f1f]">
						Could not load available times. Please try again.
					</p>
				)}

				{isEmpty && <EmptyState />}

				{allDays.length > 0 && (
					<div className="mt-8">
						<p className="font-semibold tracking-[0.16em] uppercase text-xs mb-3 text-[#2c4239]">
							Day
						</p>
						<div className="flex items-center gap-2">
							<ChevronButton
								direction="left"
								onClick={goLeft}
								disabled={!canGoLeft}
							/>
							<div className="flex flex-1 gap-2">
								{visibleDays.map((d) => {
									const isActive = d.date === selectedDate;
									return (
										<button
											key={d.date}
											type="button"
											onClick={() => setSelectedDate(d.date)}
											className={[
												"flex-1 border-b-2 px-4 py-3 text-left transition-colors",
												isActive
													? "border-[#c8a738] bg-[#d8d59f]"
													: "border-transparent hover:border-[#1a2e26]/30 hover:bg-[#d8d59f]/50",
											].join(" ")}
										>
											<div className="text-base font-semibold">
												{formatWeekday(d.date, timezone)}
											</div>
											<div className="mt-0.5 text-xs text-[#2c4239]">
												{formatMonthDay(d.date, timezone)} · {d.slots.length}{" "}
												slot
												{d.slots.length === 1 ? "" : "s"}
											</div>
										</button>
									);
								})}
								{Array.from({ length: skeletonCount }).map((_, i) => (
									<div
										key={i}
										className="h-[4.125rem] flex-1 animate-pulse rounded-md bg-[#d8d59f]/60"
									/>
								))}
							</div>
							<ChevronButton
								direction="right"
								onClick={goRight}
								disabled={!canGoRight}
							/>
						</div>

						<div className="mt-8">
							<p className="font-semibold tracking-[0.16em] uppercase text-xs mb-3 text-[#2c4239]">
								{selectedDay
									? `${formatWeekday(selectedDay.date, timezone)}, ${formatMonthDay(selectedDay.date, timezone)}`
									: ""}
							</p>
							<div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
								{selectedDay?.slots.map((slot) => (
									<button
										key={slot.start}
										type="button"
										onClick={() => onPick(slot, advisorName, advisorId)}
										className="rounded-full border border-[#1a2e26]/30 px-4 py-2.5 text-sm font-semibold transition-all hover:border-[#1a2e26] hover:bg-[#17372e] hover:text-[#e0e1b7] focus:outline-none focus:ring-2 focus:ring-[#c8a738]/50"
									>
										{formatTime(slot.start, timezone)}
									</button>
								))}
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}

type ChevronButtonProps = {
	direction: "left" | "right";
	onClick: () => void;
	disabled: boolean;
};

function ChevronButton({ direction, onClick, disabled }: ChevronButtonProps) {
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#1a2e26]/30 transition-colors hover:border-[#1a2e26] hover:bg-[#d8d59f] disabled:cursor-not-allowed disabled:opacity-30"
		>
			<svg
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
				className="h-4 w-4"
				aria-hidden
			>
				{direction === "left" ? (
					<path d="M15 18l-6-6 6-6" />
				) : (
					<path d="M9 18l6-6-6-6" />
				)}
			</svg>
		</button>
	);
}

function SlotSkeleton() {
	return (
		<div className="mt-8">
			<div className="h-3 w-8 mb-3 animate-pulse rounded bg-[#d8d59f]/60" />
			<div className="flex items-center gap-2">
				<div className="h-10 w-10 shrink-0 animate-pulse rounded-full bg-[#d8d59f]/60" />
				<div className="flex flex-1 gap-2">
					{Array.from({ length: WINDOW_SIZE }).map((_, i) => (
						<div
							key={i}
							className="h-[4.125rem] flex-1 animate-pulse rounded-md bg-[#d8d59f]/60"
						/>
					))}
				</div>
				<div className="h-10 w-10 shrink-0 animate-pulse rounded-full bg-[#d8d59f]/60" />
			</div>
			<div className="mt-8">
				<div className="h-3 w-32 mb-3 animate-pulse rounded bg-[#d8d59f]/60" />
				<div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
					{Array.from({ length: 8 }).map((_, i) => (
						<div
							key={i}
							className="h-10 animate-pulse rounded-full bg-[#d8d59f]/60"
						/>
					))}
				</div>
			</div>
		</div>
	);
}

function EmptyState() {
	return (
		<div className="mt-10 border-t border-b border-[#1a2e26]/15 px-6 py-10 text-center">
			<p className="font-semibold tracking-[0.16em] uppercase text-xs text-[#9c8230]">
				No availability
			</p>
			<p className="mt-2 text-base">No times available. Check back later.</p>
		</div>
	);
}
